// スプライト対応表と画像の読み込み。
// 絵は public/art/ の WebP（原本は art-src/ 等。tools/art/optimize.sh で縮小・変換する）。
// 新しい絵を足すときは原本を置いて optimize.sh を実行し、SHEETS と下の対応表に1行足すだけで盤面・図鑑・カードに反映される。
// 読み込みは段階的：起動時は BOOT_SHEETS（＋出撃キャラの立ち絵）だけ。敵・建物はバトルに入るとき（preloadStage）、
// HTML のアイコンは CSS の背景画像なので表示したときにブラウザが読む。
import { DEX_FIT } from './data/dex-fit';
import { BATTLE_CONFIG, CHARS, CHAR_IDS, ENEMIES, SHIKIGAMI, EQUIPMENT, EQUIP_LINES, MEIBUTSU, OMAMORI, gradeOfTier, type BuildingType, type CharId, type EnemyRole } from '@hyakki/core';
import { rem } from './ui/viewport';

export interface SheetDef {
  src: string;
  cols: number;
  rows: number;
}
/** 画像シート（等分割）。ファイルが無いシートは読み込み失敗として扱い、プレースホルダーで描く。 */
const CH_SHEETS = Object.fromEntries(
  Array.from({ length: 10 }, (_, i) => {
    const n = String(i + 1).padStart(2, '0');
    return [`ch${n}`, { src: `/art/enemies/ch${n}.webp`, cols: 5, rows: 2 }];
  }),
) as Record<`ch${string}`, SheetDef>;

export const SHEETS = {
  heroes: { src: '/art/heroes.webp', cols: 2, rows: 2 },
  // CSS 用の正方形のちびキャラ（heroes の各セルの中央を切り出したもの。セルは3:2なので CSS で縦横比が崩れないように）
  chibi_akari: { src: '/art/chibi/akari.webp', cols: 1, rows: 1 },
  chibi_kazamaru: { src: '/art/chibi/kazamaru.webp', cols: 1, rows: 1 },
  chibi_suzune: { src: '/art/chibi/suzune.webp', cols: 1, rows: 1 },
  chibi_yuzuha: { src: '/art/chibi/yuzuha.webp', cols: 1, rows: 1 },
  // 妖力 参の「覚醒の姿」（heroes と同じ 2×2 の並び）と CSS 用の正方形（カットイン）
  heroes_awake: { src: '/art/heroes-awake.webp', cols: 2, rows: 2 },
  awake_akari: { src: '/art/awake-chibi/akari.webp', cols: 1, rows: 1 },
  awake_kazamaru: { src: '/art/awake-chibi/kazamaru.webp', cols: 1, rows: 1 },
  awake_suzune: { src: '/art/awake-chibi/suzune.webp', cols: 1, rows: 1 },
  awake_yuzuha: { src: '/art/awake-chibi/yuzuha.webp', cols: 1, rows: 1 },
  world: { src: '/art/world.webp', cols: 3, rows: 2 },
  buildings: { src: '/art/buildings.webp', cols: 4, rows: 2 },
  /** 攻撃の建物（藁人形・焙烙台・大筒。4〜8は候補） */
  buildings2: { src: '/art/buildings2.webp', cols: 4, rows: 2 },
  // 龍脈（龍の石柱の霊泉。櫓と見分けやすく。art-src/gen-ryumyaku.sh の案B）
  relay: { src: '/art/relay.webp', cols: 1, rows: 1 },
  relay_blue: { src: '/art/relay-blue.webp', cols: 1, rows: 1 },
  relay_red: { src: '/art/relay-red.webp', cols: 1, rows: 1 },
  // 敵陣営の建物（同じ並びで朱と黒に描き直したもの）
  buildings_red: { src: '/art/buildings-red.webp', cols: 4, rows: 2 },
  buildings2_red: { src: '/art/buildings2-red.webp', cols: 4, rows: 2 },
  rivals: { src: '/art/enemies/rivals.webp', cols: 5, rows: 2 },
  // 各章の大将（敵ヒーロー）の盤上のちびキャラ。味方の heroes と同じ形式（1マス600×400）・同じ大きさで描く。並びは1〜10章（tools/art/leaders-sheet.mjs）
  leaders: { src: '/art/leaders.webp', cols: 5, rows: 2 },
  portrait_akari: { src: '/art/portraits/akari.webp', cols: 1, rows: 1 },
  portrait_kazamaru: { src: '/art/portraits/kazamaru.webp', cols: 1, rows: 1 },
  portrait_suzune: { src: '/art/portraits/suzune.webp', cols: 1, rows: 1 },
  portrait_yuzuha: { src: '/art/portraits/yuzuha.webp', cols: 1, rows: 1 },
  ...CH_SHEETS,
  // プロローグ（三百年前・最終戦）の人間の軍
  prologue: { src: '/art/enemies/prologue.webp', cols: 5, rows: 2 },
  shuten: { src: '/art/prologue/shuten.webp', cols: 1, rows: 1 },
  // 盤面：章の地面プレート（縦2:3）と障害物（4×3）、資源アイコン（宝箱など）
  ...Object.fromEntries(['meadow', 'river', 'bamboo', 'temple', 'snow', 'volcano', 'grave', 'mountain', 'oni', 'capital'].map((t) => [`plate_${t}`, { src: `/art/board/${t}.webp`, cols: 1, rows: 1 }])),
  // 全画面の戦場（縦 9:19.5。盤の周囲まで地続きの背景。無ければ地面プレートをぼかして広げる）
  ...Object.fromEntries(['meadow', 'river', 'bamboo', 'temple', 'snow', 'volcano', 'grave', 'mountain', 'oni', 'capital'].map((t) => [`field_${t}`, { src: `/art/battlefield/${t}.webp`, cols: 1, rows: 1 }])),
  decor: { src: '/art/board/decor.webp', cols: 4, rows: 3 },
  resicons: { src: '/art/ui/resource-icons.webp', cols: 4, rows: 2 },
  // 戦闘エフェクトの部品（4×4、battle/fx.ts の FX_CELL の並び）
  fx: { src: '/art/fx.webp', cols: 4, rows: 4 },
  // 落ちた本陣の炎上（4×4：大・中・小の炎の揺らめき各4コマ、火の粉など）
  fx2: { src: '/art/fx2.webp', cols: 4, rows: 4 },
  // 術（自分を中心に放つもの）のエフェクト（art-src/gen-fx-jutsu.sh。4×4、術ごとに本体と部品）
  fxJutsu: { src: '/art/fx-jutsu.webp', cols: 4, rows: 4 },
  // 敵の城の結界（1行目＝ゆらめく4コマ、2行目＝割れて砕ける4コマ）
  fxBarrier: { src: '/art/fx-barrier.webp', cols: 4, rows: 2 },
  // 妖力の奥義（4×4：0〜7＝本体のコマ送り、8〜15＝部品。art-src/gen-ougi.sh）。奥義を覚えていれば戦闘の開始時に読む
  ...Object.fromEntries(['akari', 'kazamaru', 'suzune', 'yuzuha', 'shuten'].map((c) => [`ougi_${c}`, { src: `/art/ougi/fx-${c}.webp`, cols: 4, rows: 4 }])),
} as Record<string, SheetDef>;
/** 奥義のカットインの一枚絵（横3:1） */
export const ougiCutinSrc = (charId: string) => `/art/ougi/cutin-${charId}.webp`;
export type SheetKey = string;

export interface SpriteRef {
  sheet: SheetKey;
  col: number;
  row: number;
  /** 足元の位置（セル下端からの割合）。余白の多い絵の調整用 */
  foot?: number;
  scale?: number;
}

export const HERO_SPRITES: Record<CharId, SpriteRef> = Object.fromEntries(
  Object.values(CHARS).map((c) => [c.id, { sheet: 'heroes', col: c.sprite.col, row: c.sprite.row, foot: 0.08 }]),
) as Record<CharId, SpriteRef>;

/** 妖力 参の覚醒の姿（Canvas 用）。シートが読めなければ HERO_SPRITES＋強いオーラで代わりに描く */
export const AWAKE_SPRITES: Record<CharId, SpriteRef> = Object.fromEntries(
  Object.values(CHARS).map((c) => [c.id, { sheet: 'heroes_awake', col: c.sprite.col, row: c.sprite.row, foot: 0.08 }]),
) as Record<CharId, SpriteRef>;
/** HTML 用：覚醒の姿の正方形（読めないときは通常のちびキャラ） */
export const awakeStyle = (id: CharId) => (failed.has(`awake_${id}`) ? heroStyle(id) : spriteStyle({ sheet: `awake_${id}` as SheetKey, col: 0, row: 0 }));

/** 縦長の立ち絵（TOP用）。無ければ heroes.png のちびキャラを拡大して使う。 */
export const PORTRAITS: Record<CharId, SpriteRef> = {
  akari: { sheet: 'portrait_akari', col: 0, row: 0 },
  kazamaru: { sheet: 'portrait_kazamaru', col: 0, row: 0 },
  suzune: { sheet: 'portrait_suzune', col: 0, row: 0 },
  yuzuha: { sheet: 'portrait_yuzuha', col: 0, row: 0 },
};

export const WORLD = {
  coreBlue: { sheet: 'world', col: 0, row: 0, foot: 0.08 },
  coreRed: { sheet: 'world', col: 1, row: 0, foot: 0.08 },
} satisfies Record<string, SpriteRef>;

/** 龍脈：buildings.png の右下。無ければ world.png の灯籠。 */
export const RELAY_SPRITES: SpriteRef[] = [
  { sheet: 'relay', col: 0, row: 0, foot: 0.04 },
  { sheet: 'buildings', col: 3, row: 1, foot: 0.06 },
  { sheet: 'world', col: 0, row: 1, foot: 0.1, scale: 0.85 },
];

/** 敵ID → 絵の候補（先頭から、読み込めたシートのものを使う）。
 *  e001〜e100 は art/enemies/chNN.png（5列×2行、data/enemies.ts の順。章ボスは各章10番目）、
 *  r01〜r10 は art/enemies/rivals.png。world.png にある数体は生成待ちの間の代わり。 */
const ENEMY_FALLBACK: Partial<Record<string, SpriteRef>> = {
  e010: { sheet: 'world', col: 2, row: 0, foot: 0.06, scale: 1.1 }, // 大たぬきの霊
  e001: { sheet: 'world', col: 2, row: 0, foot: 0.06, scale: 0.8 }, // 豆狸
  e002: { sheet: 'world', col: 1, row: 1, foot: 0.08 }, // 子河童
  e011: { sheet: 'world', col: 1, row: 1, foot: 0.08 }, // 河童
  e003: { sheet: 'world', col: 2, row: 1, foot: 0.08 }, // からかさ小僧
};
export const ENEMY_SPRITES: Record<string, SpriteRef[]> = Object.fromEntries(
  Object.values(ENEMIES).map((e) => {
    const list: SpriteRef[] = [];
    if (e.kind === 'rival') {
      const i = e.no - 101;
      list.push({ sheet: 'rivals', col: i % 5, row: Math.floor(i / 5), foot: 0.04 });
    } else if (e.id === 'h00') {
      // プロローグで操作する酒呑童子（三百年前の姿のちびキャラ、1枚絵。art-src/gen-shuten-chibi.sh）
      list.push({ sheet: 'shuten', col: 0, row: 0, foot: 0.02 });
    } else if (e.kind === 'prologue') {
      // プロローグの人間の軍（5×2）
      const i = e.no - 201;
      list.push({ sheet: 'prologue', col: i % 5, row: Math.floor(i / 5), foot: 0.04, scale: e.role === 'boss' ? 1.15 : 1 });
    } else {
      const i = (e.no - 1) % 10;
      const ch = String(Math.floor((e.no - 1) / 10) + 1).padStart(2, '0');
      list.push({ sheet: `ch${ch}`, col: i % 5, row: Math.floor(i / 5), foot: 0.04, scale: e.role === 'boss' ? 1.15 : 1 });
    }
    const fb = ENEMY_FALLBACK[e.id];
    if (fb) list.push(fb);
    return [e.id, list];
  }),
);

/** 建物 → 絵（buildings.png、4列×2行）。 */
export const BUILDING_SPRITES: Partial<Record<BuildingType, SpriteRef>> = {
  yagura: { sheet: 'buildings', col: 0, row: 0, foot: 0.06 },
  sakaku: { sheet: 'buildings', col: 1, row: 0, foot: 0.06 },
  nobori: { sheet: 'buildings', col: 2, row: 0, foot: 0.06 },
  otoshiana: { sheet: 'buildings', col: 3, row: 0, foot: 0.1 },
  shuzen: { sheet: 'buildings', col: 0, row: 1, foot: 0.06 },
  torii: { sheet: 'buildings', col: 1, row: 1, foot: 0.06 },
  kitsunebi: { sheet: 'buildings', col: 2, row: 1, foot: 0.06 },
  wara: { sheet: 'buildings2', col: 0, row: 0, foot: 0.06 },
  horoku: { sheet: 'buildings2', col: 1, row: 0, foot: 0.06 },
  ozutsu: { sheet: 'buildings2', col: 2, row: 0, foot: 0.06 },
};

/** 候補のうち、読み込み済みのシートにある最初の絵（Canvas 用）。 */
export function firstLoaded(refs: (SpriteRef | undefined)[] | undefined): SpriteRef | undefined {
  return refs?.find((r) => r && images.has(r.sheet));
}
/** 候補のうち、読み込みに失敗していないシートの最初の絵（HTML 用。背景画像なので未読込でもよい）。 */
export function firstUsable(refs: (SpriteRef | undefined)[] | undefined): SpriteRef | undefined {
  return refs?.find((r) => r && SHEETS[r.sheet] && !failed.has(r.sheet));
}
export const enemySprite = (id: string) => firstLoaded(ENEMY_SPRITES[id]);

/** 各章の大将（敵ヒーロー）の盤上の絵（leaders.webp、味方と同じ形式）。並びは tools/art/leaders-sheet.mjs の LEADER_ORDER */
const LEADER_ORDER = ['r01', 'e020', 'r03', 'r04', 'e050', 'e060', 'e070', 'r08', 'e090', 'e100'];
const LEADER_SPRITES: Record<string, SpriteRef> = Object.fromEntries(LEADER_ORDER.map((id, i) => [id, { sheet: 'leaders', col: i % 5, row: Math.floor(i / 5), foot: 0.08 }]));
/** 大将の絵（読み込めていなければ undefined → 呼び出し側は enemySprite へ） */
export const leaderSprite = (id: string) => (LEADER_SPRITES[id] ? firstLoaded([LEADER_SPRITES[id]]) : undefined);
/** 大将の顔（正方形の枠に、3:2 のマスの真ん中を切り出す）。カットインの丸い顔 */
export function leaderFaceStyle(id: string): string {
  const r = LEADER_SPRITES[id];
  if (!r) return '';
  const { cols, rows, src } = SHEETS.leaders;
  const x = ((r.col * 1.5 + 0.25) / (cols * 1.5 - 1)) * 100;
  const y = rows > 1 ? (r.row / (rows - 1)) * 100 : 0;
  return `background-image:url(${src});background-size:${cols * 150}% ${rows * 100}%;background-position:${x}% ${y}%;`;
}
export const relaySprite = () => firstLoaded(RELAY_SPRITES);
export const buildingSprite = (t: BuildingType) => firstLoaded([BUILDING_SPRITES[t]]);
/** 敵の建物：赤黒の版（読めなければ undefined → 呼び出し側が通常の絵に赤を重ねる） */
export const enemyBuildingRef = (t: BuildingType): SpriteRef | undefined => {
  const r = BUILDING_SPRITES[t];
  return r ? { ...r, sheet: `${r.sheet}_red` as SheetKey } : undefined;
};
export const enemyBuildingSprite = (t: BuildingType) => firstLoaded([enemyBuildingRef(t)]);

/** 盤上の建物の短い名前・色。名前と説明は core（BATTLE_CONFIG.buildings）が正本 */
const BUILDING_STYLE: Record<BuildingType, { short: string; color: string }> = {
  yagura: { short: '櫓', color: '#b0763a' },
  sakaku: { short: '柵', color: '#6c8f3a' },
  nobori: { short: '幟', color: '#c8463a' },
  otoshiana: { short: '穴', color: '#5b4a3a' },
  shuzen: { short: '修', color: '#8a6f4e' },
  torii: { short: '鳥', color: '#d0412f' },
  kitsunebi: { short: '灯', color: '#e09a2e' },
  wara: { short: '藁', color: '#8a5ab0' },
  horoku: { short: '焙', color: '#c0602a' },
  ozutsu: { short: '筒', color: '#6f7a3a' },
};
export const BUILDING_INFO = Object.fromEntries(
  (Object.keys(BUILDING_STYLE) as BuildingType[]).map((t) => [t, { name: BATTLE_CONFIG.buildings[t].name, desc: BATTLE_CONFIG.buildings[t].desc, ...BUILDING_STYLE[t] }]),
) as Record<BuildingType, { name: string; short: string; desc: string; color: string }>;

/** 役割ごとの色（プレースホルダーの丸・図鑑の枠）。色だけに頼らず、文字（小・中・強・主・将）も併記する。 */
export const ROLE_STYLE: Record<EnemyRole, { fill: string; ring: string; label: string }> = {
  small: { fill: '#5d8a4a', ring: '#c9e2a8', label: '小' },
  medium: { fill: '#4c5f9e', ring: '#b8c6f2', label: '中' },
  elite: { fill: '#b1452f', ring: '#f5b9a0', label: '強' },
  boss: { fill: '#9c7a1c', ring: '#ffe08a', label: '主' },
  rival: { fill: '#6e1f3a', ring: '#f39ab8', label: '将' },
};

// ---------- 読み込み ----------
const images = new Map<SheetKey, HTMLImageElement>();
const loading = new Map<SheetKey, Promise<HTMLImageElement | null>>();
const failed = new Set<SheetKey>();

export function loadSheet(key: SheetKey): Promise<HTMLImageElement | null> {
  const cached = loading.get(key);
  if (cached) return cached;
  failed.delete(key);
  const p = new Promise<HTMLImageElement | null>((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      images.set(key, img);
      resolve(img);
    };
    img.onerror = () => {
      failed.add(key);
      // 一時的な失敗（回線）は次に頼まれたとき読み直す（先読みで失敗したまま初戦が仮の絵にならないように）
      if (SHEETS[key]) loading.delete(key);
      resolve(null); // 無くてもプレースホルダーで続行
    };
    const def = SHEETS[key];
    if (!def) return img.onerror(new Event('error'));
    img.src = def.src;
  });
  loading.set(key, p);
  return p;
}
export const sheet = (key: SheetKey) => images.get(key) ?? null;
export const isLoaded = (key: SheetKey) => images.has(key);
export const hasFailed = (key: SheetKey) => failed.has(key);

/** 起動時に読むシート（TOP・盤面の地形と本陣・ちびキャラ） */
export const BOOT_SHEETS: SheetKey[] = ['heroes', 'world'];
export const portraitKey = (id: CharId) => PORTRAITS[id].sheet;

/** まとめて読む。onProgress(読めた数, 全体)。timeoutMs を過ぎたら待たずに返す（残りは裏で読み続ける） */
export async function loadSheets(keys: SheetKey[], onProgress?: (done: number, total: number) => void, timeoutMs = 8000): Promise<void> {
  const list = [...new Set(keys)];
  let done = 0;
  onProgress?.(0, list.length);
  const all = Promise.all(
    list.map((k) =>
      loadSheet(k).then(() => {
        done++;
        onProgress?.(done, list.length);
      }),
    ),
  );
  await Promise.race([all, new Promise((r) => setTimeout(r, timeoutMs))]);
}

/** 敵ID → 絵のシート（chNN / rivals） */
export const enemySheetKeys = (enemyId: string) => (ENEMY_SPRITES[enemyId] ?? []).map((r) => r.sheet);

/** バトルに入る前に読むシート：建物・本陣・ちびキャラ・敵の大将（rivals）＋その戦場に出る敵の章シート */
export function stageSheets(enemyIds: string[], terrain?: string): SheetKey[] {
  return [...BOOT_SHEETS, 'buildings', 'buildings2', 'relay', 'relay_blue', 'relay_red', 'buildings_red', 'buildings2_red', 'rivals', 'leaders', 'decor', 'resicons', 'fx', 'fx2', 'fxJutsu', 'fxBarrier', ...(terrain ? [`plate_${terrain}`, `field_${terrain}`] : []), ...enemyIds.flatMap((id) => enemySheetKeys(id).slice(0, 1))];
}

/** 障害物マスの絵（decor.webp の番号）：地形ごとに合う物を選ぶ。
 *  0松 1広葉樹 2岩 3大岩／4池 5竹 6雪岩 7溶岩岩／8墓石 9石灯籠 10茂み 11木の橋 */
export const DECOR_BY_TERRAIN: Record<string, number[]> = {
  meadow: [0, 1, 2, 10, 1, 3],
  river: [4, 1, 2, 0, 10],
  bamboo: [5, 5, 2, 10],
  temple: [9, 0, 2, 10],
  snow: [6, 0, 6, 3],
  volcano: [7, 3, 7, 2],
  grave: [8, 9, 10, 8],
  mountain: [3, 0, 2, 3],
  oni: [7, 3, 2],
  capital: [9, 10, 1, 9],
};

/** sprite を足元基準で描く。描けなければ false（呼び出し側がプレースホルダーを描く）。 */
export function drawSprite(ctx: CanvasRenderingContext2D, ref: SpriteRef | undefined, x: number, bottomY: number, size: number, alpha = 1): boolean {
  if (!ref) return false;
  const img = images.get(ref.sheet);
  if (!img) return false;
  const def = SHEETS[ref.sheet];
  const sw = img.width / def.cols;
  const sh = img.height / def.rows;
  const k = (size * (ref.scale ?? 1)) / Math.max(sw, sh);
  const w = sw * k;
  const h = sh * k;
  const prev = ctx.globalAlpha;
  ctx.globalAlpha = prev * alpha;
  ctx.drawImage(img, ref.col * sw, ref.row * sh, sw, sh, x - w / 2, bottomY - h + h * (ref.foot ?? 0), w, h);
  ctx.globalAlpha = prev;
  return true;
}

/** 色を重ねた絵（敵の建物を赤くするなど）。1コマずつ色ごとに作って取っておく */
const tinted = new Map<string, HTMLCanvasElement>();
export function drawSpriteTinted(ctx: CanvasRenderingContext2D, ref: SpriteRef | undefined, x: number, bottomY: number, size: number, tint: string, amount: number): boolean {
  if (!ref) return false;
  const img = images.get(ref.sheet);
  if (!img) return false;
  const def = SHEETS[ref.sheet];
  const sw = img.width / def.cols;
  const sh = img.height / def.rows;
  const key = `${ref.sheet}:${ref.col}:${ref.row}:${tint}:${amount}`;
  let c = tinted.get(key);
  if (!c) {
    c = document.createElement('canvas');
    c.width = Math.round(sw);
    c.height = Math.round(sh);
    const g = c.getContext('2d')!;
    g.drawImage(img, ref.col * sw, ref.row * sh, sw, sh, 0, 0, c.width, c.height);
    g.globalCompositeOperation = 'source-atop';
    g.globalAlpha = amount;
    g.fillStyle = tint;
    g.fillRect(0, 0, c.width, c.height);
    tinted.set(key, c);
  }
  const k = (size * (ref.scale ?? 1)) / Math.max(sw, sh);
  const w = sw * k;
  const h = sh * k;
  ctx.drawImage(c, x - w / 2, bottomY - h + h * (ref.foot ?? 0), w, h);
  return true;
}

/** 絵の無い敵：役割色の丸に名前の頭文字。 */
export function drawEnemyPlaceholder(ctx: CanvasRenderingContext2D, name: string, role: EnemyRole, x: number, cy: number, r: number, alpha = 1) {
  const st = ROLE_STYLE[role];
  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.beginPath();
  ctx.arc(x, cy, r, 0, Math.PI * 2);
  const g = ctx.createRadialGradient(x - r * 0.3, cy - r * 0.4, r * 0.1, x, cy, r);
  g.addColorStop(0, lighten(st.fill));
  g.addColorStop(1, st.fill);
  ctx.fillStyle = g;
  ctx.fill();
  ctx.lineWidth = Math.max(2, r * 0.12);
  ctx.strokeStyle = st.ring;
  ctx.stroke();
  ctx.fillStyle = '#fff8e8';
  ctx.font = `700 ${Math.round(r * 1.05)}px "Shippori Mincho B1", "Noto Sans JP", serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(name.slice(0, 1), x, cy + r * 0.05);
  // 役割の文字（色だけに頼らない）
  ctx.font = `700 ${Math.max(8, Math.round(r * 0.45))}px "Noto Sans JP", sans-serif`;
  ctx.fillStyle = '#1b1208';
  const bx = x + r * 0.72;
  const by = cy - r * 0.72;
  ctx.beginPath();
  ctx.arc(bx, by, r * 0.36, 0, Math.PI * 2);
  ctx.fillStyle = st.ring;
  ctx.fill();
  ctx.fillStyle = '#1b1208';
  ctx.fillText(st.label, bx, by + 0.5);
  ctx.restore();
}

function lighten(hex: string) {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.min(255, ((n >> 16) & 255) + 60);
  const g = Math.min(255, ((n >> 8) & 255) + 60);
  const b = Math.min(255, (n & 255) + 60);
  return `rgb(${r},${g},${b})`;
}

/** HTML 用：シートの1コマを背景にする style 文字列。 */
export function spriteStyle(ref: SpriteRef): string {
  const def = SHEETS[ref.sheet];
  const x = def.cols > 1 ? (ref.col / (def.cols - 1)) * 100 : 0;
  const y = def.rows > 1 ? (ref.row / (def.rows - 1)) * 100 : 0;
  return `background-image:url(${def.src});background-size:${def.cols * 100}% ${def.rows * 100}%;background-position:${x}% ${y}%;`;
}
export const heroStyle = (id: CharId) => spriteStyle({ sheet: `chibi_${id}` as SheetKey, col: 0, row: 0 });

/** 顔アイコン（art/ui/faces.webp、2×2。並びは heroes と同じ） */
export function faceStyle(id: CharId): string {
  const s = CHARS[id].sprite;
  return `background-image:url(/art/ui/faces.webp);background-size:200% 200%;background-position:${s.col * 100}% ${s.row * 100}%;`;
}
export const faceHtml = (id: CharId, size = 48, cls = '') =>
  `<span class="face ${cls}" style="width:${rem(size)};height:${rem(size)};${faceStyle(id)}" role="img" aria-label="${CHARS[id].name}"></span>`;

/** 敵の小さなアイコン（HTML）。絵があれば絵、無ければ頭文字の丸。 */
export function enemyIconHtml(enemyId: string, size = 40): string {
  const e = ENEMIES[enemyId];
  if (!e) return '';
  const ref = firstUsable(ENEMY_SPRITES[enemyId]);
  if (ref) return `<span class="enemy-icon" style="width:${rem(size)};height:${rem(size)};${spriteStyle(ref)}" role="img" aria-label="${e.name}"></span>`;
  const st = ROLE_STYLE[e.role];
  return `<span class="enemy-icon ph" style="width:${rem(size)};height:${rem(size)};--fill:${st.fill};--ring:${st.ring}" role="img" aria-label="${e.name}（${st.label}）"><b>${e.name.slice(0, 1)}</b><i>${st.label}</i></span>`;
}

/** 式神のカード用の大きな絵（art/shiki/<id>.webp。art-src/gen-shiki-hd.sh）。無い妖は戦闘のちび絵 */
export const SHIKI_ART = new Set<string>(['e008', 'e009', 'e010', 'e018', 'e019', 'e028', 'e029', 'e030', 'e038', 'e039', 'e040', 'e048', 'e049', 'e058', 'e059', 'e068', 'e069', 'e078', 'e079', 'e080', 'e088', 'e089', 'e098', 'e099', 'r02', 'r05', 'r06', 'r07', 'r09', 'r10']);
/** 式神（10種族）の進化の段階（★）を返す関数。state が起動時に入れる（assets は state に依存しない） */
let shikiStageOf: (id: string) => number = () => 1;
export const setShikiStageGetter = (f: (id: string) => number) => (shikiStageOf = f);
/** 式神の進化の姿の絵（art/shiki/<種族>-<★>.webp。art-src/gen-shiki-forms.sh）。star を省くと持っている式神の今の★ */
export const shikiFormSrc = (id: string, star?: number) => {
  const key = SHIKIGAMI[id]?.key;
  return key ? `/art/shiki/${key}-${Math.max(1, Math.min(3, star ?? shikiStageOf(id)))}.webp` : null;
};
export function shikiArtHtml(id: string, size = 56, star?: number): string {
  const form = shikiFormSrc(id, star);
  if (form) return `<img class="shiki-art" src="${form}" alt="${SHIKIGAMI[id]?.forms[Math.max(1, Math.min(3, star ?? shikiStageOf(id))) - 1] ?? ''}" width="${size}" height="${size}" style="width:${rem(size)};height:${rem(size)}" decoding="async" draggable="false">`;
  if (!SHIKI_ART.has(id)) return enemyIconHtml(id, size);
  return `<img class="shiki-art" src="/art/shiki/${id}.webp" alt="${ENEMIES[id]?.name ?? ''}" width="${size}" height="${size}" style="width:${rem(size)};height:${rem(size)}" decoding="async" draggable="false">`;
}

/** 図鑑のマスで絵の大きさをそろえる（data/dex-fit.ts。shikiArtHtml と同じ絵を選んで、その絵の拡大率とずらし） */
export function dexFitStyle(id: string, star?: number): string {
  const ref = firstUsable(ENEMY_SPRITES[id]);
  const key = shikiFormSrc(id, star) ?? (SHIKI_ART.has(id) ? `/art/shiki/${id}.webp` : ref ? `${ref.sheet === 'rivals' ? 'rivals' : ref.sheet}:${ref.row * SHEETS[ref.sheet].cols + ref.col}` : '');
  return fitStyle(DEX_FIT[key]);
}
/** 図鑑の「？？？」の影（いつも戦闘のちび絵） */
export function dexFitSpriteStyle(id: string): string {
  const ref = firstUsable(ENEMY_SPRITES[id]);
  return ref ? fitStyle(DEX_FIT[`${ref.sheet}:${ref.row * SHEETS[ref.sheet].cols + ref.col}`]) : '';
}
/** シートの1マスの絵を、その絵の中身の大きさ・真ん中でそろえる（建物図面など。data/dex-fit.ts にあるマスだけ） */
export const spriteFitStyle = (ref: SpriteRef) => fitStyle(DEX_FIT[`${ref.sheet}:${ref.row * SHEETS[ref.sheet].cols + ref.col}`]);
const fitStyle = (f?: { s: number; x: number; y: number }) => (f ? `transform:translate(${(f.x * 100).toFixed(1)}%,${(f.y * 100).toFixed(1)}%) scale(${f.s});` : '');

/** TOPの立ち絵（HTML）。縦長の立ち絵があればそれ、無ければ（読めなければ）ちびキャラの拡大。
 *  読み込み中は空の枠（呼び出し側が loadSheet(portraitKey(id)) のあとに描き直す）。 */
export function portraitHtml(id: CharId, cls = 'portrait'): string {
  const p = PORTRAITS[id];
  if (!images.has(p.sheet) && !failed.has(p.sheet)) return `<span class="${cls} pending" role="img" aria-label="${CHARS[id].name}"></span>`;
  if (images.has(p.sheet)) return `<img class="${cls} tall" src="${SHEETS[p.sheet].src}" alt="${CHARS[id].name}" draggable="false">`;
  return `<span class="${cls} chibi" style="${heroStyle(id)}" role="img" aria-label="${CHARS[id].name}"></span>`;
}

// ---------- 道具アイコン（art/ui/item-icons.webp・omamori-icons.webp、4×3） ----------
const cell43 = (src: string, i: number) =>
  `background-image:url(${src});background-size:400% 300%;background-position:${((i % 4) / 3) * 100}% ${(Math.floor(i / 4) / 2) * 100}%;`;
/** 戦闘の道具のイラスト（art/ui/battle-items.webp、4×3。tools/art/battle-items.mjs で作る）：回復薬・力の薬・守りの薬・韋駄天の薬／清め札・妖力の薬・（候補6つ） */
const BATTLE_ITEM_ORDER = ['heal', 'power', 'guard', 'swift', 'purify', 'spirit'];
export const battleItemArt = (id: string) => cell43('/art/ui/battle-items.webp', Math.max(0, BATTLE_ITEM_ORDER.indexOf(id)));
/** item-icons の並び：提灯杖・羽団扇・大槌・刀／胴丸・陣羽織・根付・勾玉根付／巻物・木槌・砥石・宝箱 */
export const ITEM_CELL = { scroll: 8, mallet: 9, whetstone: 10, chest: 11 } as const;
export function itemCell(defId: string): number {
  const d = EQUIPMENT[defId];
  if (!d) return ITEM_CELL.chest;
  if (d.slot === 'weapon') return Math.max(0, CHAR_IDS.indexOf(d.charId ?? 'akari'));
  if (d.slot === 'armor') return d.enemyId ? 5 : 4;
  return d.enemyId ? 7 : 6;
}
/** 装備の絵（art/ui/items.webp、12列×13行。tools/art/item-atlas.mjs で作る）：系統×6段階の格 → 名物24 → 妖の装備2 */
const ITEMS_COLS = 12;
const ITEMS_ROWS = 13;
export function itemArtIndex(defId: string): number | null {
  const d = EQUIPMENT[defId];
  if (!d) return null;
  if (d.line) return EQUIP_LINES.findIndex((l) => l.id === d.line) * 6 + gradeOfTier(d.tier);
  if (d.meibutsu) return EQUIP_LINES.length * 6 + (MEIBUTSU.find((m) => m.id === d.id)?.art ?? 0);
  if (d.enemyId) return EQUIP_LINES.length * 6 + MEIBUTSU.length + (d.slot === 'armor' ? 0 : 1);
  return null;
}
/** 数字＝道具アイコン（item-icons の巻物・木槌など）、装備ID＝装備の絵 */
/** 装備の札の色分け：キャラ専用（own own-<キャラ>）か共用（shared）と、レアリティの枠（r1〜r4）。.thumb.it に付ける */
const RAR_NUM: Record<string, number> = { n: 1, r: 2, sr: 3, ssr: 4 };
export function itemFrameCls(defId: string, rarity: string): string {
  const c = EQUIPMENT[defId]?.charId;
  return `it r${RAR_NUM[rarity] ?? 1} ${c ? `own own-${c}` : 'shared'}${EQUIPMENT[defId]?.meibutsu ? ' meibutsu' : ''}`;
}
/** キャラ専用の装備：左下にそのキャラの顔（共用は何も出さない） */
export function itemOwnerBadge(defId: string): string {
  const c = EQUIPMENT[defId]?.charId;
  return c ? `<i class="it-own" style="${faceStyle(c)}" title="${CHARS[c]?.name ?? ''}専用"></i>` : '';
}
export const itemIconStyle = (cellOrDefId: number | string) => {
  if (typeof cellOrDefId === 'number') return cell43('/art/ui/item-icons.webp', cellOrDefId);
  const i = itemArtIndex(cellOrDefId);
  if (i == null) return cell43('/art/ui/item-icons.webp', itemCell(cellOrDefId));
  return `background-image:url(/art/ui/items.webp);background-size:${ITEMS_COLS * 100}% ${ITEMS_ROWS * 100}%;background-position:${((i % ITEMS_COLS) / (ITEMS_COLS - 1)) * 100}% ${(Math.floor(i / ITEMS_COLS) / (ITEMS_ROWS - 1)) * 100}%;`;
};
/** お守り：omamori.ts の基本12種の順。外伝品は元のお守りの絵 */
const OM_ORDER = ['sai2', 'nidobushin', 'hayagaeri', 'jintori', 'kusuribukuro', 'shirozeme', 'onitaiji', 'dodai', 'takuwae', 'kikkou', 'yokebarai', 'kazaguruma'];
export function omamoriIconStyle(defId: string) {
  const base = OMAMORI[defId]?.sameAs ?? defId;
  return cell43('/art/ui/omamori-icons.webp', Math.max(0, OM_ORDER.indexOf(base)));
}
// ---------- おまもりガチャ・お守りの共通の絵（art/omikuji は旧名のまま。art-src/gen-omikuji.sh → tools/art/optimize.sh omikuji） ----------
const RIDX: Record<string, number> = { n: 0, r: 1, sr: 2, ssr: 3 };
const row = (src: string, cols: number, i: number) =>
  `background-image:url(${src});background-size:${cols * 100}% 100%;background-position:${cols > 1 ? (i / (cols - 1)) * 100 : 0}% 0;background-repeat:no-repeat;`;
/** 小物：0 御朱印帳・1 錬成の香炉・2 鈴・3 絵馬・4 お札・5 宝珠（ピックアップ）・6（使わない）・7 錠前 */
export const OMK_ICON = { goshuin: 0, fuse: 1, suzu: 2, ema: 3, ofuda: 4, pickup: 5, lock: 7 } as const;
/** おまもりガチャの絵（art-src/gacha/make-gacha-icons.py でお守りの絵から合成）：お守り3つ（看板・演出）・ガチャ券 */
export const gachaArt = {
  omamori: 'background-image:url(/art/ui/gacha-omamori.webp);background-size:contain;background-position:center;background-repeat:no-repeat;',
  ticket: 'background-image:url(/art/ui/gacha-ticket.webp);background-size:contain;background-position:center;background-repeat:no-repeat;',
};
export const omikujiArt = {
  /** お守りの台紙（並・良・優・極） */
  daishi: (rarity: string) => row('/art/omikuji/daishi.webp', 4, RIDX[rarity] ?? 0),
  icon: (i: number) =>
    `background-image:url(/art/omikuji/icons.webp);background-size:400% 200%;background-position:${((i % 4) / 3) * 100}% ${Math.floor(i / 4) * 100}%;background-repeat:no-repeat;`,
  /** 光（4×4、黒地＝screen 合成）：0-3 金朱の爆発、4-7 紫、8-11 青、12 金の紙吹雪・13 朱白の紙吹雪・14 桜・15 星 */
  fx: (i: number) =>
    `background-image:url(/art/omikuji/fx.webp);background-size:400% 400%;background-position:${((i % 4) / 3) * 100}% ${(Math.floor(i / 4) / 3) * 100}%;background-repeat:no-repeat;`,
};

/** 章の絵地図 */
export const mapSrc = (chapter: number) => `/art/maps/ch${String(chapter).padStart(2, '0')}.webp`;
